import { inject } from '@angular/core'
import { Router } from '@angular/router'

import { BehaviorSubject, Observable, takeUntil } from 'rxjs'

import { BillingMode, CountryCodes, PricingPlans, Tsp } from '@ti/contracts'
import { AccessControl } from '@ti/core/common/services'
import { checkExistApiError, getApiErrorKey } from '@ti/core/common/utils'
import { DIALOG_FACADE } from '@ti/core/contracts/dialog'
import { ListModel } from '@ti/core/models/list.model'
import { ListModelState } from '@ti/core/models/types'
import { getSettingsByCountryCode } from '@ti/core/ui-kit/i18n'

import { TspForm } from '../../account/contracts'

export type TspAccountsState<T> = ListModelState<T> & {
  errorMessage$: Observable<string>
}

export abstract class BaseAccountsModel<T extends Tsp> extends ListModel<T, TspAccountsState<T>> {
  public readonly launchErrorMessage$ = new BehaviorSubject<string>('')
  public readonly showLaunchForm$ = new BehaviorSubject<boolean>(false)
  public readonly showEditForm$ = new BehaviorSubject<boolean>(false)
  public readonly launchingAccount$ = new BehaviorSubject<boolean>(false)
  public readonly editingAccount$ = new BehaviorSubject<boolean>(false)

  protected readonly errorMessage$ = new BehaviorSubject<string>('')
  protected readonly dialogFacade = inject(DIALOG_FACADE)
  protected readonly accessControl = inject(AccessControl)
  protected readonly router = inject(Router)

  public override loadState(): TspAccountsState<T> {
    return {
      ...super.loadState(),
      errorMessage$: this.errorMessage$.pipe(takeUntil(this.destroy$)),
    }
  }

  openFleetLaunchDialog() {
    this.launchErrorMessage$.next('')
    this.showLaunchForm$.next(true)
  }

  openEditDialog(account: T) {
    this.select(account)
    this.showEditForm$.next(true)
  }

  async launchAccount(account: TspForm) {
    this.launchingAccount$.next(true)

    const isLaunched = await this.callLaunchApi(account)
    if (isLaunched) {
      this.showLaunchForm$.next(false)
    }

    this.launchingAccount$.next(false)
  }

  async editAccount(account: TspForm) {
    this.editingAccount$.next(true)
    if (await this.callUpdateApi(account)) {
      this.showEditForm$.next(false)
      this.editingAccount$.next(false)
    }
    this.editingAccount$.next(false)
  }

  protected async callLaunchApi(data: TspForm) {
    this.errorMessage$.next('')

    try {
      await this.api.ti.accounts.launch(this.prepareTspData(data))
      this.reset()
      this.messageService.add({
        summary: await this.languageService.translate(
          'ti-admin.accounts.notifications.launched-title',
        ),
        detail: await this.languageService.translate(
          'ti-admin.accounts.notifications.launched-summary',
          {
            email: data.email,
          },
        ),
        life: 5000,
        severity: 'success',
      })
      this.errorMessage$.next('')
      return true
    } catch (e: any) {
      console.error(e)
      await this.setErrorMessage(e.message)
      this.reset()
      return false
    }
  }

  protected async callUpdateApi(data: TspForm) {
    this.errorMessage$.next('')
    const selectedFleet = this.store.selectedItem$.value
    const id = selectedFleet?.id
    if (!id) {
      await this.setErrorMessage()
      return false
    }

    try {
      const updatedFleet = await this.api.ti.accounts.update(id, this.prepareTspData(data))

      this.reset()
      this.select({
        ...selectedFleet,
        ...updatedFleet,
      })

      this.messageService.add({
        summary: await this.languageService.translate('ti-admin.accounts.notifications.updated', {
          name: data.name,
        }),
        life: 5000,
        severity: 'success',
      })
      this.errorMessage$.next('')
      return true
    } catch (e: any) {
      console.error(e)
      await this.setErrorMessage(e.message)
      return false
    }
  }

  protected async confirmDelete(data: Tsp) {
    const labels = await this.getDeleteDialogLabels(data.name)
    const dialogResult = await this.dialogFacade.confirm({
      ...labels,
      confirmButtonColor: 'var(--color-alert-500)',
      confirmButtonIcon: 'pi-trash',
    })
    if (dialogResult) {
      try {
        await this.api.ti.accounts.deleteAccount(data.id)

        this.messageService.add({ summary: labels.deleted, life: 5000, severity: 'success' })
        this.reset()
        this.reloadCount()
      } catch (error) {
        await this.errorHandler.notifyUser(error, 'Cannot delete TSP')
      }
    }
  }

  protected async getDeleteDialogLabels(name: string) {
    const [summary, description, confirmationMessage, confirmButton, cancelButton, deleted] =
      await Promise.all([
        this.languageService.translate('ti-admin.accounts.delete-dialog.summary', { name }),
        this.languageService.translate('ti-admin.accounts.delete-dialog.description'),
        this.languageService.translate('ti-admin.accounts.delete-dialog.delete-confirm-text'),
        this.languageService.translate('ti-admin.accounts.delete-dialog.confirm-button'),
        this.languageService.translate('ti-admin.accounts.delete-dialog.cancel-button'),
        this.languageService.translate('ti-admin.accounts.notifications.deleted', { name }),
      ])
    return {
      summary,
      description,
      confirmationMessage,
      confirmButton,
      cancelButton,
      deleted,
    }
  }

  protected async getSuspendDialogLabels(name: string) {
    const [summary, description, confirmButton, cancelButton] = await Promise.all([
      this.languageService.translate('ti-admin.accounts.suspend-dialog.summary', { name }),
      this.languageService.translate('ti-admin.accounts.suspend-dialog.description'),
      this.languageService.translate('ti-admin.accounts.suspend-dialog.confirm-button'),
      this.languageService.translate('ti-admin.accounts.suspend-dialog.cancel-button'),
    ])
    return { summary, description, confirmButton, cancelButton }
  }

  protected async setErrorMessage(message = 'ti-admin.accounts.errors.unknown') {
    if (checkExistApiError(message)) {
      message = getApiErrorKey(message)
    } else {
      message = 'ti-admin.accounts.errors.unknown'
    }
    this.errorMessage$.next(await this.languageService.translate(message))
  }

  protected async showUnknownErrorNotification() {
    this.messageService.add({
      summary: await this.languageService.translate('ti-admin.accounts.errors.unknown'),
      severity: 'error',
      life: 5000,
    })
  }

  protected prepareTspData(data: TspForm) {
    const rates = {
      tntDevicePrice: data.tntDevicePrice,
      standardDevicePrice: data.standardDevicePrice,
      advancedDevicePrice: data.advancedDevicePrice,
      premiumDevicePrice: data.premiumDevicePrice,
      standardMediaDevicePrice: data.standardMediaDevicePrice,
      advancedMediaDevicePrice: data.advancedMediaDevicePrice,
      premiumMediaDevicePrice: data.premiumMediaDevicePrice,
      extraTelemetryPrice: data.billingMode === BillingMode.Monthly ? data.extraTelemetryPrice : 0,
      extraStoragePrice: data.billingMode === BillingMode.Monthly ? data.extraStoragePrice : 0,
    }
    const billingConfig = {
      plans: {
        [PricingPlans.TnT]: data.tntPlan,
        [PricingPlans.Standard]: data.standardPlan,
        [PricingPlans.Advanced]: data.advancedPlan,
        [PricingPlans.Premium]: data.premiumPlan,
      },
      rates,
      limits: {
        telemetryHistory: data.billingMode === BillingMode.Annual ? data.telemetryHistory : 0,
        storageSize: data.billingMode === BillingMode.Annual ? data.storageSize : 0,
      },
      minimumMonthlyFee: data?.minimumMonthlyFee ?? 0,
    }

    let billingStartDate: Date | null | undefined = data.billingStartDate ?? null

    if (billingStartDate) {
      billingStartDate = new Date(billingStartDate)

      const y = billingStartDate.getFullYear()
      const m = billingStartDate.getMonth()
      const d = billingStartDate.getDate()
      billingStartDate = new Date(Date.UTC(y, m, d, 0, 1, 0, 0))
    }

    return {
      name: data.name,
      legalName: data.legalName,
      billingMode: data.billingMode,
      billingStartDate,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone,
      country: data.country,
      language: getSettingsByCountryCode(data.country as CountryCodes).lang,
      address: data.address,
      city: data.city,
      region: data.region,
      zipCode: data.zipCode,
      usernamePrefix: data.usernamePrefix ?? null,
      billingConfig,
    }
  }
}
